import React, { createRef, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  View,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Platform,
  TouchableOpacity,
  KeyboardAvoidingView,
  Keyboard,
} from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { useAuthData } from "../../hooks/useAuthData";
import { RootState } from "../../redux/reducers";
import { translate } from "../../i18n/translate";
import { DomusoApiService } from "../../api/DomusoApiService";
import { addReservations, addUser, signOutUser } from "../../redux/actions/actionCreators";
import Constants from "expo-constants";
import { Grid, Container, Row, Column } from "ui-grid";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import {
  SimpleAccordion,
  Text,
  typography,
  theme,
  TextInput,
  ErrorWarningBottomSheet,
  Button,
} from "ui-components";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { yupResolver } from "@hookform/resolvers/yup";
import { emailVerificationSchema, profileInfoSchema } from "./utils/validation-schemas-my-account";
import { formatPhoneString, formatCognitoPhoneNumber } from "../../utils/phoneNumber";
import { useForm } from "react-hook-form";
import { ToggleEmailPreferences } from "../../components/new/my-account/toggle-email-preferences";
import { DeleteMyUnit } from "../../components/new/my-account/delete-my-unit";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { UserFields, EmailVerificationFields, EmailPreferencesFields } from "./utils/account-enums";
import { refreshSessionAndTokens } from "./utils/refresh-session-and-tokens";
import { getUnitNumber } from "./utils/helper";

interface ErrorMsgObjectProps {
  title?: string;
  body?: string;
}

export const MyAccount = ({ route }: any) => {
  const logger = useSelector((state: RootState) => state.logger);
  const [updateUserInfo, setUpdateUserInfo] = useState(true);
  const { userInfo } = useAuthData(logger, updateUserInfo);
  const dispatch = useDispatch();
  const { family_name, given_name, email, domusoId, phone_number } = userInfo || {};
  const navigation = useNavigation();
  const { params } = route;

  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const reservations = useSelector((state: RootState) => state.reservation.reservations);

  const [isDeleteAccountLoading, setDeleteAccountLoading] = useState<any>(false);

  const [expandPersonalInfoAccordion, setExpandPersonalInfoAccordion] = useState(false);
  const [expandEmailPreferencesAccordion, setExpandEmailPreferencesAccordion] = useState(false);
  const [expandMyUnitsAccordion, setExpandMyUnitsAccordion] = useState(false);
  const [selectedPropertyDetails, setSelectedPropertyDetails] = useState<any>({});
  const [errorMsgObject, setErrorMsgObject] = useState<ErrorMsgObjectProps>({});

  const refDeleteUnitAlert = useRef<any>();
  const refDeleteAccountAlert = useRef<any>();
  const refVerifyEmailAddressAlert = useRef<any>();
  const refPopupError = useRef<any>();
  const refPopupErrorCompleteEmailVerification = useRef<any>();
  const refPopupErrorDeleteAccount = useRef<any>();
  const refPopupErrorDeleteUnit = useRef<any>();

  //
  const [firstName, setFirstName] = useState(given_name);
  const [lastName, setLastName] = useState(family_name);
  const [emailState, setEmailState] = useState(email);
  const [phoneString, setPhoneString] = useState();

  const [isLoading, setLoading] = useState(false);
  const [loadingEmailPrefValues, setLoadingEmailPrefValues] = useState(false);
  const [isRemoveLoading, setRemoveLoading] = useState(false);

  const inputAccessoryViewID = "inputAccessoryViewID1";
  const ref = [createRef(), createRef(), createRef(), createRef(), createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [uuidReturnedFromResponse, setUuidReturnedFromResponse] = useState();
  const [isMonthlyRentReminderToggleEnabled, setIsMonthlyRentReminderToggleEnabled] =
    useState(true);
  const [isPromotionsToggleEnabled, setIsPromotionsToggleEnabled] = useState(false);
  const [emailPrefValuesUpdatedToggle, setEmailPrefValuesUpdatedToggle] = useState(true);
  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(profileInfoSchema),
  });

  const {
    register: register2,
    trigger: trigger2,
    handleSubmit: handleSubmit2,
    setValue: setValue2,
    getValues: getValues2,
    clearErrors: clearErrors2,
    formState: { errors: errors2 },
  } = useForm({
    resolver: yupResolver(emailVerificationSchema),
  });

  const handlePhoneFieldUpdate = (field: string, val: string) => {
    const formatted = formatPhoneString(val);
    const numberValWithoutCountryCode = formatCognitoPhoneNumber(val, "");
    setPhoneString(formatted);
    clearErrors(field);
    setValue(field, numberValWithoutCountryCode);
  };

  const handleFieldUpdate = (field: string, val: string) => {
    clearErrors(field);
    setValue(field, val);
    if (field == UserFields.FIRST_NAME) {
      setFirstName(val);
    } else if (field == UserFields.LAST_NAME) {
      setLastName(val);
    } else if (field == UserFields.EMAIL) {
      setEmailState(val);
    }
  };

  const getSaveChangesDisabled = () => {
    const { first_name, last_name, email: currentEmail, phone } = getValues();
    if (
      currentEmail !== email ||
      first_name !== given_name ||
      last_name !== family_name ||
      (phone && phone !== phone_number?.slice(2))
    ) {
      return false;
    } else {
      return true;
    }
  };
  useEffect(() => {
    register(UserFields.FIRST_NAME);
    register(UserFields.LAST_NAME);
    register(UserFields.EMAIL);
    register(UserFields.PHONE);
  }, [register]);

  useEffect(() => {
    register2(EmailVerificationFields.VERIFICATION_CODE);
  }, [register2]);

  useEffect(() => {
    setFirstName(given_name);
    setLastName(family_name);
    if (phone_number) {
      handlePhoneFieldUpdate(UserFields.PHONE, phone_number?.slice(2));
    }
    setValue(UserFields.FIRST_NAME, given_name);
    setValue(UserFields.LAST_NAME, family_name);
  }, [given_name, family_name, phone_number]);

  useEffect(() => {
    setEmailState(email);
    setValue(UserFields.EMAIL, email);
  }, [email]);

  const showUnexpectedError = (error?: any) => {
    setLoading(false);
    if (
      error &&
      (error == "networkError" ||
        error?.message == "Failed to fetch" ||
        error?.message == "Network request failed")
    ) {
      setErrorMsgObject({
        title: translate("myAccount.connectionErrorTitle") as string,
        body: translate("myAccount.connectionErrorMessage") as string,
      });
      return;
    } else {
      setErrorMsgObject({
        title: translate("myAccount.error") as string,
        body: translate("myAccount.unexpectedError") as string,
      });
      return;
    }
  };

  const onContinueHandler = async () => {
    const { first_name, last_name, email: currentEmail, phone } = getValues();
    try {
      Keyboard.dismiss();
      if (
        first_name !== given_name ||
        last_name !== family_name ||
        phone !== phone_number?.slice(2)
      ) {
        await updatePersonalInfo();
      }
      if (currentEmail !== email) {
        updatePersonalInfoEmailInitiate();
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const updatePersonalInfo = async () => {
    const { first_name, last_name, email: currentEmail, phone } = getValues();
    try {
      setLoading(true);
      const body = {
        userId: domusoId,
        currentEmail: email,
        firstName: first_name,
        lastName: last_name,
        phone: phone,
      };
      const res = await domusoApiService.updatePersonalInfo(body);
      await refreshSessionAndTokens(dispatch);
      setUpdateUserInfo(!updateUserInfo);
      setLoading(false);
    } catch (error) {
      showUnexpectedError(error);
      refPopupError?.current?.open();
      setLoading(false);
    }
  };
  const updatePersonalInfoEmailInitiate = async () => {
    const { email } = getValues();
    try {
      setLoading(true);
      const body = {
        email: email,
        userId: domusoId,
      };
      const res = await domusoApiService.updatePersonalInfoEmailInitiate(body);
      setUuidReturnedFromResponse(res?.body?.uuid);
      refVerifyEmailAddressAlert?.current?.open();
      setLoading(false);
    } catch (error) {
      showUnexpectedError(error);
      if (error?.message == "Internal server error") {
        refVerifyEmailAddressAlert?.current?.open();
      } else {
        refPopupError?.current?.open();
      }
      setLoading(false);
    }
  };

  const updatePersonalInfoEmailComplete = async () => {
    try {
      const { email } = getValues();
      const { verification_code } = getValues2();
      setLoading(true);
      const body = {
        uuid: uuidReturnedFromResponse,
        otc: verification_code,
      };
      const res = await domusoApiService.updatePersonalInfoEmailComplete(body);
      setValue2(EmailVerificationFields.VERIFICATION_CODE, "");
      setVerificationCode("");
      if (res?.body?.valid === false) {
        setErrorMsgObject({
          title: translate("myAccount.error") as string,
          body: translate("myAccount.invalidVerificationCodeError") as string,
        });
        refPopupErrorCompleteEmailVerification?.current?.open();
        setLoading(false);
        return;
      }
      refVerifyEmailAddressAlert?.current?.close();
      await refreshSessionAndTokens(dispatch);
      dispatch(
        addUser({ cognitoUser: cognitoUser, userInfo: { username: email }, sessionExpired: false })
      );
      setUpdateUserInfo(!updateUserInfo);
      setLoading(false);
    } catch (error) {
      setValue2(EmailVerificationFields.VERIFICATION_CODE, "");
      setVerificationCode("");
      if (error?.message == "Internal server error") {
        setErrorMsgObject({
          title: translate("myAccount.error") as string,
          body: translate("myAccount.failedToUpdateEmailError") as string,
        });
      } else {
        showUnexpectedError(error);
      }
      refPopupErrorCompleteEmailVerification?.current?.open();
      setLoading(false);
    }
  };

  const deleteAccount = () => {
    setDeleteAccountLoading(true);
    //We are using the disable account flow.
    return domusoApiService
      .disableAccount()
      .then(async (resp) => {
        await logger.log({ resp });
        dispatch(signOutUser());
      })
      .catch((e) => {
        logger.error(e);
        showUnexpectedError(e);
        refPopupErrorDeleteAccount?.current?.open();
      })
      .finally(() => {
        refDeleteAccountAlert?.current?.close();
        setDeleteAccountLoading(false);
      });
  };

  const updateEmailPreferencesState = (
    emailPreferenceType: EmailPreferencesFields,
    value: boolean
  ) => {
    if (emailPreferenceType == EmailPreferencesFields.ALLOW_MARKETING_CAMPAIGNS) {
      setIsPromotionsToggleEnabled(value);
    }
    if (emailPreferenceType == EmailPreferencesFields.RENTAL_REMINDERS) {
      setIsMonthlyRentReminderToggleEnabled(value);
    }
  };
  const updateUsersEmailPreferences = async (
    emailPreferenceType: EmailPreferencesFields,
    value: boolean
  ) => {
    try {
      setLoading(true);
      const body: any = {
        userId: domusoId,
        currentEmail: email,
        firstName,
      };
      body[emailPreferenceType] = value;
      updateEmailPreferencesState(emailPreferenceType, value);
      // console.log(body);
      const res = await domusoApiService.updatePersonalInfo(body);
      // if (!res?.body?.success) {
      //   updateEmailPreferencesState(emailPreferenceType, !value);
      // }
      setEmailPrefValuesUpdatedToggle(!emailPrefValuesUpdatedToggle);
      setLoading(false);
      return;
    } catch (error) {
      // console.log("updateUsersEmailPreferences error: ", error);
      updateEmailPreferencesState(emailPreferenceType, !value);
      showUnexpectedError(error);
      refPopupError?.current?.open();
      setLoading(false);
    }
  };

  const onRemoveConfirmation = async (resIdToRemove: number | string | any) => {
    setRemoveLoading(true);
    try {
      logger.info(`Hiding reservation with id ${resIdToRemove}`);
      await domusoApiService.hideReservation(resIdToRemove);
      const newReservations = reservations.filter((res: any) => res.id !== resIdToRemove);
      dispatch(addReservations(newReservations));
      setRemoveLoading(false);
      refDeleteUnitAlert?.current?.close();
    } catch (error) {
      logger.error(error);
      setRemoveLoading(false);
      showUnexpectedError(error);
      refPopupErrorDeleteUnit?.current?.open();
    }
  };

  useEffect(() => {
    setLoadingEmailPrefValues(true);
    (async () => {
      try {
        let accountDetails = await domusoApiService.getAccountDetails();
        setIsPromotionsToggleEnabled(accountDetails?.allow_marketing_campaigns);
        setIsMonthlyRentReminderToggleEnabled(accountDetails?.rental_reminders);
      } catch (error) {
        logger.error(error);
      }
      setLoadingEmailPrefValues(false);
    })();
  }, [emailPrefValuesUpdatedToggle]);

  const onPressChangePassword = () => {
    navigation.navigate("Actions", { screen: "ChangePassword", params: { ...params } });
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={Platform.OS === "web" ? undefined : 155}>
      <SafeAreaView style={styles.container}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollableArea}>
          <View style={{ flex: 1 }}>
            <View style={{ marginTop: 20 }} />
            <Grid>
              <Container>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column
                    span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                    style={[styles.inputRow, styles.header, styles.accordionPadding]}>
                    <CommonAccordionMyAccount
                      title={translate("myAccount.personalInformation")}
                      styles={styles}
                      expanded={expandPersonalInfoAccordion}
                      onPress={() => {
                        setExpandPersonalInfoAccordion(!expandPersonalInfoAccordion);
                      }}
                      {...automationTestingLocators.myAccount.personalInformationAccordion}>
                      <View style={styles.accordionChildrenContainer}>
                        <InputRow
                          value={firstName}
                          formName={UserFields.FIRST_NAME}
                          label={translate("myAccount.firstName")}
                          inputAccessoryViewID={inputAccessoryViewID}
                          ref={ref[0] as any}
                          errors={errors}
                          styles={styles}
                          handleFieldUpdate={handleFieldUpdate}
                          onFocus={() => setCurrentInputIndex(0)}
                          onSubmitEditing={() => (ref[1].current as any).focus()}
                          returnKeyType="next"
                          {...automationTestingLocators.myAccount.firstNameTextInput}
                        />
                        <View style={{ marginTop: 8 }} />
                        <InputRow
                          value={lastName}
                          formName={UserFields.LAST_NAME}
                          label={translate("myAccount.lastName")}
                          inputAccessoryViewID={inputAccessoryViewID}
                          ref={ref[1] as any}
                          errors={errors}
                          styles={styles}
                          handleFieldUpdate={handleFieldUpdate}
                          onFocus={() => setCurrentInputIndex(1)}
                          onSubmitEditing={() => (ref[2].current as any).focus()}
                          returnKeyType="next"
                          {...automationTestingLocators.myAccount.lastNameTextInput}
                        />
                        <View style={{ marginTop: 8 }} />
                        <InputRow
                          value={emailState}
                          formName={UserFields.EMAIL}
                          type="email-address"
                          label={translate("myAccount.email")}
                          inputAccessoryViewID={inputAccessoryViewID}
                          ref={ref[2] as any}
                          errors={errors}
                          styles={styles}
                          handleFieldUpdate={handleFieldUpdate}
                          onFocus={() => setCurrentInputIndex(2)}
                          onSubmitEditing={() => (ref[3].current as any).focus()}
                          returnKeyType="next"
                          {...automationTestingLocators.myAccount.emailTextInput}
                        />
                        <View style={{ marginTop: 8 }} />
                        <InputRow
                          placeholder={"(000) 000 -0000"}
                          formName={UserFields.PHONE}
                          type="phone-pad"
                          label={translate("myAccount.phone")}
                          inputAccessoryViewID={inputAccessoryViewID}
                          errors={errors}
                          styles={styles}
                          value={phoneString}
                          handleFieldUpdate={handlePhoneFieldUpdate}
                          ref={ref[3] as any}
                          onFocus={() => setCurrentInputIndex(3)}
                          returnKeyType="go"
                          onSubmitEditing={handleSubmit(onContinueHandler)}
                          {...automationTestingLocators.myAccount.phoneTextInput}
                        />
                      </View>
                    </CommonAccordionMyAccount>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column
                    span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                    style={[styles.inputRow, styles.accordionPadding]}>
                    <View style={{ marginTop: 20 }} />
                    <CommonAccordionMyAccount
                      title={translate("myAccount.emailPreferences") as string}
                      styles={styles}
                      expanded={expandEmailPreferencesAccordion}
                      onPress={() => {
                        setExpandEmailPreferencesAccordion(!expandEmailPreferencesAccordion);
                      }}
                      {...automationTestingLocators.myAccount.emailPreferencesAccordion}>
                      <View style={[styles.accordionChildrenContainer, { paddingHorizontal: 0 }]}>
                        <ToggleEmailPreferences
                          title={translate("myAccount.monthlyRentReminders") as string}
                          value={isMonthlyRentReminderToggleEnabled}
                          onValueChange={(value: boolean) => {
                            updateUsersEmailPreferences(
                              EmailPreferencesFields.RENTAL_REMINDERS,
                              value
                            );
                          }}
                          disabled={isLoading || loadingEmailPrefValues}
                          {...automationTestingLocators.myAccount.monthlyRentReminders}
                        />
                        <View style={{ marginTop: 10 }} />
                        <ToggleEmailPreferences
                          title={translate("myAccount.promotionsAndSurveys") as string}
                          value={isPromotionsToggleEnabled}
                          onValueChange={(value: boolean) => {
                            updateUsersEmailPreferences(
                              EmailPreferencesFields.ALLOW_MARKETING_CAMPAIGNS,
                              value
                            );
                          }}
                          disabled={isLoading || loadingEmailPrefValues}
                          {...automationTestingLocators.myAccount.promotionsAndSurveys}
                        />
                        <View style={styles.emailPreferenceNoteContainer}>
                          <Text
                            style={styles.emailPreferenceNote1}
                            {...automationTestingLocators.myAccount.emailPreferencesNote}>
                            <Text style={styles.emailPreferenceNote2}>
                              {translate("myAccount.emailPreferenceNote1") as string}
                            </Text>
                            {translate("myAccount.emailPreferenceNote2") as string}
                          </Text>
                        </View>
                      </View>
                    </CommonAccordionMyAccount>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column
                    span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                    style={[styles.inputRow, styles.accordionPadding]}>
                    <View style={{ marginTop: 20 }} />
                    <CommonAccordionMyAccount
                      title={translate("myAccount.myUnits") as string}
                      styles={styles}
                      expanded={expandMyUnitsAccordion}
                      onPress={() => {
                        setExpandMyUnitsAccordion(!expandMyUnitsAccordion);
                      }}
                      {...automationTestingLocators.myAccount.myUnitsAccordion}>
                      <View style={[styles.accordionChildrenContainer, { paddingHorizontal: 0 }]}>
                        {reservations && reservations?.length > 0 ? (
                          reservations?.map((reservation: any, index: number) => (
                            <>
                              <DeleteMyUnit
                                key={reservation?.id}
                                reservationLength={reservations.length}
                                propertyName={reservation?.prop_name}
                                propertyUnit={reservation?.property_unit}
                                onPressDelete={(props: any) => {
                                  setSelectedPropertyDetails(props);
                                  refDeleteUnitAlert?.current?.open();
                                }}
                                id={reservation?.id}
                                {...automationTestingLocators.myAccount.deleteMyUnit}
                              />
                            </>
                          ))
                        ) : (
                          <View style={styles.noUnitsAssignedContainer}>
                            <Text style={styles.noUnitsAssignedText}>
                              {translate("myAccount.noUnitsAssigned")}
                            </Text>
                          </View>
                        )}
                      </View>
                    </CommonAccordionMyAccount>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column
                    span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                    style={[styles.inputRow, styles.accordionPadding]}>
                    <View style={{ marginTop: 20 }} />
                    <CommonAccordionMyAccount
                      title={translate("myAccount.changePassword") as string}
                      styles={styles}
                      expanded={expandMyUnitsAccordion}
                      onPress={onPressChangePassword}
                      accordionType={"button"}
                      {...automationTestingLocators.myAccount.changePasswordButton}
                    />
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                    <View style={styles.appVersionTextContainer}>
                      <Text
                        style={styles.appVersionText}
                        {...automationTestingLocators.myAccount.appVersionText}>
                        {`${translate("myAccount.appVersion") as string} (${Platform?.OS}): ${
                          Constants?.expoConfig?.version
                        }`}
                      </Text>
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                    <View style={styles.deleteAccountContainer}>
                      <TouchableOpacity
                        activeOpacity={0.6}
                        onPress={() => {
                          refDeleteAccountAlert?.current?.open();
                        }}
                        style={styles.deleteAccountButton}
                        {...automationTestingLocators.myAccount.deleteAccountButton}>
                        <View style={styles.deleteAccountTextContainer}>
                          <Text
                            style={styles.deleteAccountText}
                            {...automationTestingLocators.myAccount.deleteAccountText}>
                            {translate("myAccount.deleteAccount") as string}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
              </Container>
            </Grid>
          </View>
        </ScrollView>
        <BottomNav backgroundColor={theme.colors.white}>
          <View style={{ marginTop: 15 }} />
          <Row>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
            <Column span={{ xs: 12, sm: 12, md: 9, lg: 7 }}>
              <Button
                text={translate("myAccount.cancelButton") as string}
                style={{ width: "100%", paddingTop: 12, paddingBottom: 12 }}
                type="secondary"
                onPress={() => {
                  navigation?.goBack();
                }}
                {...automationTestingLocators.myAccount.cancelButton}
              />
              <View style={{ marginTop: 10 }} />
              <Button
                text={translate("myAccount.saveChangesButton") as string}
                style={{ width: "100%", paddingTop: 12, paddingBottom: 12 }}
                textStyle={{ fontFamily: "Lato_700Bold" }}
                onPress={handleSubmit(onContinueHandler)}
                disabled={isLoading || getSaveChangesDisabled()}
                loading={isLoading}
                {...automationTestingLocators.myAccount.saveChangesButton}
              />
            </Column>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
          </Row>
        </BottomNav>
        <Footer />

        <ErrorWarningBottomSheet
          height={420}
          alertType="warning"
          ref={refVerifyEmailAddressAlert}
          title={translate("myAccount.verifyEmailAlertTitle") as string}
          body={translate("myAccount.verifyEmailAlertBody") as string}
          leftButtonType={"secondary"}
          rightButtonType={"primary"}
          leftButtonText={translate("myAccount.verifyEmailAlertCancelButton") as string}
          rightButtonText={translate("myAccount.verifyEmailAlertConfirmButton") as string}
          rightButtonTextStyle={{ fontFamily: "Lato_700Bold" }}
          buttonsContainer={{ flexDirection: "column" }}
          containerStyleModal={{ maxWidth: 400 }}
          onPressLeftButton={() => refVerifyEmailAddressAlert?.current?.close()}
          onPressRightButton={handleSubmit2(updatePersonalInfoEmailComplete)}
          rightButtonLoading={isLoading}
          leftButtonDisabled={isLoading}
          rightButtonDisabled={isLoading}
          bodyTextStyle={styles.alertBodyText}
          leftButtonStyle={styles.leftButtonVerificationAlert}
          rightButtonStyle={styles.rightButtonAlert}
          {...automationTestingLocators.myAccount.verifyEmailAddressWarningAlert}>
          <View style={{ marginTop: 10, marginBottom: 10 }}>
            <InputRow
              formName={EmailVerificationFields.VERIFICATION_CODE}
              type="phone-pad"
              label={translate("myAccount.verificationCode") as string}
              inputAccessoryViewID={inputAccessoryViewID}
              errors={errors2}
              styles={styles}
              value={verificationCode}
              handleFieldUpdate={(formName: string, value: string) => {
                clearErrors2(formName);
                setValue2(formName, value);
                setVerificationCode(value);
              }}
              ref={ref[4] as any}
              onFocus={() => setCurrentInputIndex(4)}
              returnKeyType="go"
              onSubmitEditing={handleSubmit2(updatePersonalInfoEmailComplete)}
              placeholderStyle={styles.verificationCodeInputPlaceholderStyle}
              {...automationTestingLocators.myAccount.verificationCodeTextInput}
            />
            <ErrorWarningBottomSheet
              ref={refPopupErrorCompleteEmailVerification}
              title={errorMsgObject?.title}
              body={errorMsgObject?.body}
              height={150}
              alertType={"error"}
              showButtons={false}
              {...automationTestingLocators.myAccount.errorMsgAlertEmailVerification}
            />
          </View>
        </ErrorWarningBottomSheet>
        <ErrorWarningBottomSheet
          height={300}
          alertType="warning"
          ref={refDeleteUnitAlert}
          title={`${translate("myAccount.deleteUnitAlertTitle")} ${getUnitNumber(
            selectedPropertyDetails?.propertyUnit
          )}?`}
          body={translate("myAccount.deleteUnitAlertBody") as string}
          leftButtonType={"secondary"}
          rightButtonType={"primary"}
          leftButtonText={translate("myAccount.deleteUnitAlertDeleteButton") as string}
          rightButtonText={translate("myAccount.deleteUnitAlertCancelButton") as string}
          leftButtonTextStyle={{ color: theme.colors.alert }}
          leftButtonStyle={styles.leftButtonAlert}
          rightButtonStyle={styles.rightButtonAlert}
          rightButtonTextStyle={{ fontFamily: "Lato_700Bold" }}
          buttonsContainer={{ flexDirection: "column", marginTop: 30 }}
          containerStyleModal={{ maxWidth: 400 }}
          onPressRightButton={() => refDeleteUnitAlert?.current?.close()}
          onPressLeftButton={() => {
            onRemoveConfirmation(selectedPropertyDetails?.id);
          }}
          bodyTextStyle={styles.alertBodyText}
          leftButtonLoading={isRemoveLoading}
          rightButtonLoading={isRemoveLoading}
          leftButtonDisabled={isRemoveLoading}
          rightButtonDisabled={isRemoveLoading}
          {...automationTestingLocators.myAccount.deleteUnitWarningAlert}>
          <ErrorWarningBottomSheet
            ref={refPopupErrorDeleteUnit}
            title={errorMsgObject?.title}
            body={errorMsgObject?.body}
            height={150}
            alertType={"error"}
            showButtons={false}
            {...automationTestingLocators.myAccount.errorMsgAlertDeleteUnit}
          />
        </ErrorWarningBottomSheet>
        <ErrorWarningBottomSheet
          height={300}
          alertType="warning"
          ref={refDeleteAccountAlert}
          title={translate("myAccount.deleteAccountAlertTitle") as string}
          body={translate("myAccount.deleteAccountAlertBody") as string}
          leftButtonType={"secondary"}
          rightButtonType={"primary"}
          leftButtonText={translate("myAccount.deleteAccountAlertDeleteButton") as string}
          rightButtonText={translate("myAccount.deleteAccountAlertCancelButton") as string}
          leftButtonTextStyle={{ color: theme.colors.alert }}
          leftButtonStyle={styles.leftButtonAlert}
          rightButtonStyle={styles.rightButtonAlert}
          rightButtonTextStyle={{ fontFamily: "Lato_700Bold" }}
          buttonsContainer={{ flexDirection: "column", marginTop: 30 }}
          containerStyleModal={{ maxWidth: 400 }}
          onPressRightButton={() => refDeleteAccountAlert?.current?.close()}
          onPressLeftButton={deleteAccount}
          leftButtonLoading={isDeleteAccountLoading}
          leftButtonDisabled={isDeleteAccountLoading}
          rightButtonDisabled={isDeleteAccountLoading}
          bodyTextStyle={styles.alertBodyText}
          {...automationTestingLocators.myAccount.deleteAccountWarningAlert}>
          <ErrorWarningBottomSheet
            ref={refPopupErrorDeleteAccount}
            title={errorMsgObject?.title}
            body={errorMsgObject?.body}
            height={150}
            alertType={"error"}
            showButtons={false}
            {...automationTestingLocators.myAccount.errorMsgAlertDeleteAccount}
          />
        </ErrorWarningBottomSheet>
        <ErrorWarningBottomSheet
          ref={refPopupError}
          title={errorMsgObject?.title}
          body={errorMsgObject?.body}
          height={160}
          alertType={"error"}
          showButtons={false}
          {...automationTestingLocators.myAccount.errorMsgAlert}
        />
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

const CommonAccordionMyAccount = (props?: any) => {
  let {
    styles,
    title,
    expanded,
    onPress,
    testID,
    accessibilityLabel,
    children,
    accordionType = "accordion",
  } = props;
  return (
    <SimpleAccordion
      accordionStyle={styles.accordionStyle}
      title={<Text style={[typography.title1, styles.accordionTitle]}>{title}</Text>}
      expanded={expanded}
      rightIconColor={theme?.colors.primary}
      rightIconName={accordionType == "button" && "direction-right"}
      showLeftIcon={false}
      showRightIcon={true}
      onPress={onPress}
      testID={testID}
      accessibilityLabel={accessibilityLabel}>
      <View
        style={{
          marginHorizontal: -10,
        }}>
        {children}
      </View>
    </SimpleAccordion>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: theme.colors.white,
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: theme.colors.white,
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: theme.colors.white,
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollView: { flexGrow: 0 },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    flexDirection: "row",
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
    }),
  },
  header: {
    ...domusoResponsiveStyleHelper({
      webPhone: { paddingVertical: 3 },
      webTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      webDesktop: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      iosPhone: { paddingVertical: 3 },
      iosTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      androidPhone: { paddingVertical: 3 },
      androidTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
    }),
  },
  accordionStyle: {
    paddingLeft: 15,
    paddingBottom: 15,
    paddingTop: 15,
    paddingRight: 15,
    backgroundColor: theme.colors.smokeWhite,
  },
  accordionTitle: {
    fontFamily: "Lato_700Bold",
    color: theme.colors.secondary,
  },
  accordionColumnStyle: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  accordionChildrenContainer: {
    paddingHorizontal: 25,
    backgroundColor: theme.colors.smokeWhite,
    paddingBottom: 20,
  },
  deleteAccountContainer: {
    marginTop: 45,
    flexDirection: "row",
    justifyContent: "center",
  },
  deleteAccountButton: {
    padding: 15,
    outlineStyle: "none",
  },
  deleteAccountTextContainer: {
    borderBottomColor: theme.colors.alert,
    borderBottomWidth: 1,
  },
  deleteAccountText: {
    ...typography.subHeading,
    textAlign: "center",
    color: theme.colors.alert,
    lineHeight: 15,
    fontFamily: "Lato_700Bold",
  },
  appVersionTextContainer: {
    marginTop: 20,
  },
  appVersionText: {
    ...typography.subHeading,
    textAlign: "center",
    color: theme.colors.text2,
  },
  emailPreferenceNote1: {
    ...typography.body,
    color: theme.colors.text3,
    fontWeight: "400",
  },
  emailPreferenceNote2: {
    color: theme.colors.text3,
    fontFamily: "Lato_700Bold",
  },
  accordionPadding: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingHorizontal: 0,
  },
  emailPreferenceNoteContainer: { marginHorizontal: 25, marginTop: 10 },
  alertBodyText: {
    color: theme.colors.text3,
  },
  leftButtonVerificationAlert: {
    ...domusoResponsiveStyleHelper({
      iosTablet: { flex: 0 },
      androidTablet: { flex: 0 },
    }),
  },
  leftButtonAlert: {
    borderColor: theme.colors.alert,
    ...domusoResponsiveStyleHelper({
      iosTablet: { flex: 0 },
      androidTablet: { flex: 0 },
    }),
  },
  rightButtonAlert: {
    ...domusoResponsiveStyleHelper({
      iosTablet: { flex: 0 },
      androidTablet: { flex: 0 },
    }),
  },
  verificationCodeInputPlaceholderStyle: {
    ...domusoResponsiveStyleHelper({
      iosTablet: { flex: 0 },
      androidTablet: { flex: 0 },
    }),
  },
  noUnitsAssignedContainer: {
    paddingVertical: 20,
    backgroundColor: theme.colors.white,
  },
  noUnitsAssignedText: {
    ...typography.title2,
    textAlign: "center",
    flex: 1,
    color: theme.colors.text3,
  },
});
const InputRow = forwardRef(
  (
    {
      formName,
      label,
      type = "default",
      onFocus,
      errors,
      inputAccessoryViewID,
      handleFieldUpdate,
      styles,
      value,
      returnKeyType,
      onSubmitEditing,
      testID,
      accessibilityLabel,
      placeholder,
      maxLength,
      placeholderStyle,
    }: any,
    ref: any
  ) => (
    <TextInput
      value={value}
      labelStyle={{ fontFamily: "Lato_700Bold" }}
      label={label}
      error={errors?.[formName]}
      mode="outlined"
      keyboardType={type}
      onChangeText={(val) => handleFieldUpdate(formName, val)}
      returnKeyType={returnKeyType}
      inputAccessoryViewID={inputAccessoryViewID}
      ref={ref}
      onFocus={onFocus}
      onSubmitEditing={onSubmitEditing}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      placeholder={placeholder}
      maxLength={maxLength}
      placeholderStyle={placeholderStyle}
    />
  )
);
